import { useEffect, useState } from 'react';
import type { Me } from '../lib/api';
import { API_BASE } from '../lib/api';

/**
 * Migration Tracker — live CUCM→Webex migration tracker, sourced from the
 * Master sheet pipeline. Native real-time view (this page) + a published
 * snapshot pushed to SharePoint for the wider admin team.
 *
 * ACCESS: restricted to a named allow-list (plus super_admin). Add the admins
 * the city wants to grant. tenant_admins NOT on the list see a polite gate.
 */
const TRACKER_ALLOWLIST: string[] = [
  'ahmed.hamad@longbeach.gov',
  // + add the specific admins to grant (provided by Ahmed):
];

// Seed = the latest verified TriNetHub sync. The page tries the live endpoint
// (/api/migration/tracker) first and falls back to this if it isn't up yet,
// so the tracker always renders real numbers.
const SEED = {
  synced_at: '2026-06-23 (last full TriNetHub sync)',
  total_users: 3042,
  verdicts: [
    { key: 'ALREADY_MIGRATED', label: 'Already migrated (live in Hub)', n: 638, tone: 'ok' },
    { key: 'ON_CUCM',          label: 'On CUCM — to migrate',          n: 235, tone: 'info' },
    { key: 'SHARED_LINE',      label: 'Shared line (owner resolved)',  n: 959, tone: 'warn' },
    { key: 'WEBEX_APP',        label: 'Webex App (Jabber today)',      n: 155, tone: 'info' },
    { key: 'NEEDS_PHONE',      label: 'Needs phone (provision)',       n: 496, tone: 'order' },
    { key: 'NO_LINE',          label: 'No line (App-only / skip)',     n: 559, tone: 'muted' },
  ],
  phones_to_order: { total: 417, models: [['8851', 296], ['8841', 106], ['7811', 9], ['6851', 6]] },
  contact_center: { wxcc_agents: 412, wxcc_premium: 190, wxcc_standard: 194, uccx_ipcc: 364, migrated: 288, backlog: 76 },
  extensions: { used: 13585, free: 56489, did_8xxxx_used: 5588, did_8xxxx_free: 4412 },
  on_ao: 1029,
  sharepoint_url: '', // set when the published copy lands in SharePoint
};

type Tracker = typeof SEED;

const toneCls: Record<string, string> = {
  ok: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
  info: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
  warn: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300',
  order: 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300',
  muted: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
};

function Tile({ label, value, sub }: { label: string; value: React.ReactNode; sub?: string }) {
  return (
    <div className="card p-4">
      <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</div>
      <div className="text-2xl font-semibold mt-1 text-tnh-accent">{value}</div>
      {sub && <div className="text-xs text-gray-500 mt-0.5">{sub}</div>}
    </div>
  );
}

const STATUS = ['', 'Not started', 'In progress', 'Scheduled', 'Migrated', 'Verified', 'On hold', 'N/A'];

const ADMIN_LABELS = ['Migration Status', 'Assigned Tech', 'Scheduled Cutover', 'Verified By', 'Admin Notes'];

function TrackerGrid({ me, readOnly }: { me: Me; readOnly?: boolean }) {
  const [cols, setCols] = useState<string[]>([]);
  const [rows, setRows] = useState<any[]>([]);
  const [search, setSearch] = useState('');
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const LIMIT = 50;
  const canEdit = !readOnly && (me.role === 'super_admin' || me.role === 'tenant_admin' || me.role === 'tenant_user_rw');

  const load = (off: number, q: string) => {
    setLoading(true);
    fetch(`${API_BASE}/api/migration/tracker?search=${encodeURIComponent(q)}&offset=${off}&limit=${LIMIT}`, { credentials: 'include' })
      .then((r) => r.json())
      .then((j) => { if (j.cols) setCols(j.cols); setRows(j.rows || []); setTotal(j.total || 0); setOffset(off); })
      .catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(0, ''); /* eslint-disable-next-line */ }, []);

  const save = (k: string, field: string, value: string, idx: number) => {
    setRows((rs) => rs.map((r, i) => (i === idx ? { ...r, [field]: value, _saved: false } : r)));
    fetch(`${API_BASE}/api/migration/tracker/update`, {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_key: k, field, value }),
    }).then((r) => (r.ok ? r.json() : Promise.reject(new Error('save'))))
      .then(() => setRows((rs) => rs.map((r, i) => (i === idx ? { ...r, _saved: true } : r))))
      .catch(() => setRows((rs) => rs.map((r, i) => (i === idx ? { ...r, _err: true } : r))));
  };
  const yel = 'bg-yellow-50 dark:bg-yellow-900/20';
  const stick = 'sticky left-0 z-10 bg-tnh-card dark:bg-tnh-card-dark';

  return (
    <div className="card p-4">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div className="font-semibold">Per-user overview <span className="text-xs font-normal text-gray-500">· read-only — edit in “Migration Tracker Sheet”</span></div>
        <form onSubmit={(e) => { e.preventDefault(); load(0, search); }} className="flex gap-2">
          <input className="input-sm" placeholder="Search any field…" value={search} onChange={(e) => setSearch(e.target.value)} />
          <button className="btn-secondary" type="submit">Search</button>
        </form>
      </div>
      <div className="text-xs text-gray-500 mb-2">{loading ? 'Loading…' : `${total.toLocaleString()} users · ${total ? offset + 1 : 0}–${Math.min(offset + LIMIT, total)} · ${cols.length} system columns + 5 editable`}</div>
      <div className="overflow-auto border border-tnh-border dark:border-tnh-border-dark rounded" style={{ maxHeight: 560 }}>
        <table className="text-[11px] whitespace-nowrap">
          <thead className="sticky top-0 z-20 bg-tnh-card dark:bg-tnh-card-dark">
            <tr className="border-b border-tnh-border dark:border-tnh-border-dark">
              {cols.map((c, j) => <th key={j} className={`px-2 py-1 text-left font-semibold ${j === 0 ? stick + ' z-30' : ''}`}>{c}</th>)}
              {ADMIN_LABELS.map((l) => <th key={l} className={`px-2 py-1 text-left font-semibold ${yel}`}>{l}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.k} className={`border-b border-tnh-border/30 dark:border-tnh-border-dark/30 ${r._saved ? 'ring-1 ring-green-400' : ''}`}>
                {(r.sys || []).map((v: string, j: number) => <td key={j} className={`px-2 py-0.5 max-w-[260px] truncate ${j === 0 ? stick : ''}`} title={v}>{v}</td>)}
                <td className={`px-1 ${yel}`}>
                  <select disabled={!canEdit} value={r.status} onChange={(e) => save(r.k, 'status', e.target.value, i)} className="bg-transparent text-gray-900 dark:text-gray-100 [&>option]:text-gray-900">
                    {STATUS.map((s) => <option key={s} value={s}>{s || '—'}</option>)}
                  </select>
                </td>
                <td className={`px-1 ${yel}`}><input disabled={!canEdit} defaultValue={r.assigned_tech} onBlur={(e) => e.target.value !== r.assigned_tech && save(r.k, 'assigned_tech', e.target.value, i)} className="bg-transparent text-gray-900 dark:text-gray-100 w-24" /></td>
                <td className={`px-1 ${yel}`}><input disabled={!canEdit} type="date" defaultValue={r.cutover_date} onBlur={(e) => e.target.value !== r.cutover_date && save(r.k, 'cutover_date', e.target.value, i)} className="bg-transparent text-gray-900 dark:text-gray-100 w-28 dark:[color-scheme:dark]" /></td>
                <td className={`px-1 ${yel}`}><input disabled={!canEdit} defaultValue={r.verified_by} onBlur={(e) => e.target.value !== r.verified_by && save(r.k, 'verified_by', e.target.value, i)} className="bg-transparent text-gray-900 dark:text-gray-100 w-24" /></td>
                <td className={`px-1 ${yel}`}><input disabled={!canEdit} defaultValue={r.notes} onBlur={(e) => e.target.value !== r.notes && save(r.k, 'notes', e.target.value, i)} className="bg-transparent text-gray-900 dark:text-gray-100 w-40" /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {total > LIMIT && (
        <div className="flex gap-2 justify-center mt-3">
          <button className="btn-secondary" disabled={offset === 0 || loading} onClick={() => load(Math.max(0, offset - LIMIT), search)}>Prev</button>
          <button className="btn-secondary" disabled={offset + LIMIT >= total || loading} onClick={() => load(offset + LIMIT, search)}>Next</button>
        </div>
      )}
    </div>
  );
}

export default function MigrationTracker({ me }: { me: Me }) {
  const allowed = me.role === 'super_admin' || TRACKER_ALLOWLIST.includes((me.email || '').toLowerCase());
  const [t, setT] = useState<Tracker>(SEED);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const refresh = () => {
    setLoading(true); setErr(null);
    fetch(`${API_BASE}/api/migration/tracker`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((j) => { if (j && j.tracker) setT({ ...SEED, ...j.tracker }); })
      .catch((e) => setErr(`Live endpoint not reachable yet — showing last sync. (${e.message})`))
      .finally(() => setLoading(false));
  };
  useEffect(() => { if (allowed) refresh(); /* eslint-disable-next-line */ }, [allowed]);

  if (!allowed) {
    return (
      <div className="max-w-2xl mx-auto mt-16 card p-8 text-center">
        <div className="text-lg font-semibold">Migration Tracker — access restricted</div>
        <p className="text-sm text-gray-500 mt-2">
          This tracker is limited to named administrators. Your account
          (<span className="font-mono">{me.email}</span>) isn't on the list yet.
          Ask Ahmed (Long Beach IT) to add you.
        </p>
      </div>
    );
  }

  const cc = t.contact_center;
  return (
    <div className="p-4 md:p-6 space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-semibold">Migration Tracker</h1>
          <p className="text-sm text-gray-500">Live CUCM → Webex migration status · {t.synced_at}</p>
        </div>
        <div className="flex items-center gap-2">
          {t.sharepoint_url
            ? <a href={t.sharepoint_url} target="_blank" rel="noreferrer" className="btn-secondary">Open in SharePoint ↗</a>
            : <span className="pill pill-warn">SharePoint publish: pending setup</span>}
          <button className="btn-primary" onClick={refresh} disabled={loading}>{loading ? 'Refreshing…' : 'Refresh'}</button>
        </div>
      </div>
      {err && <div className="pill pill-warn">{err}</div>}

      {/* headline tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile label="Total users" value={t.total_users.toLocaleString()} />
        <Tile label="Already migrated" value={t.verdicts.find((v) => v.key === 'ALREADY_MIGRATED')?.n ?? '—'} sub="live Webex PEOPLE" />
        <Tile label="Phones to order" value={t.phones_to_order.total} sub="incompatible — replace" />
        <Tile label="On AO roster" value={t.on_ao.toLocaleString()} />
      </div>

      {/* verdict breakdown */}
      <div className="card p-4">
        <div className="font-semibold mb-3">Migration verdict (Milton 4-check)</div>
        <div className="space-y-2">
          {t.verdicts.map((v) => {
            const pct = Math.round((v.n / t.total_users) * 100);
            return (
              <div key={v.key} className="flex items-center gap-3">
                <div className="w-56 text-sm">{v.label}</div>
                <div className="flex-1 h-4 rounded bg-gray-100 dark:bg-gray-800 overflow-hidden">
                  <div className={`h-full ${toneCls[v.tone].split(' ')[0]}`} style={{ width: `${pct}%` }} />
                </div>
                <div className="w-16 text-right text-sm font-medium">{v.n}</div>
                <div className="w-10 text-right text-xs text-gray-500">{pct}%</div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {/* phones to order */}
        <div className="card p-4">
          <div className="font-semibold mb-3">Phones to order (not Webex-compatible)</div>
          <table className="w-full text-sm">
            <tbody>
              {t.phones_to_order.models.map(([m, n]) => (
                <tr key={m as string} className="border-b border-tnh-border/40 dark:border-tnh-border-dark/40">
                  <td className="py-1.5">Cisco MPP {m}</td>
                  <td className="py-1.5 text-right font-medium">{n}</td>
                </tr>
              ))}
              <tr><td className="py-2 font-semibold">Total</td><td className="py-2 text-right font-semibold text-orange-600">{t.phones_to_order.total}</td></tr>
            </tbody>
          </table>
        </div>

        {/* contact center */}
        <div className="card p-4">
          <div className="font-semibold mb-3">Contact Center (UCCX → WxCC)</div>
          <div className="grid grid-cols-2 gap-3">
            <Tile label="WxCC agents (live)" value={cc.wxcc_agents} />
            <Tile label="Migrated from UCCX" value={cc.migrated} sub={`${cc.backlog} still to move`} />
            <Tile label="WxCC Premium" value={cc.wxcc_premium} />
            <Tile label="WxCC Standard" value={cc.wxcc_standard} />
          </div>
        </div>
      </div>

      {/* extensions */}
      <div className="card p-4">
        <div className="font-semibold mb-3">Extension availability (CUCM · Unity · UCCX · Webex · WxCC)</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Tile label="Used (all systems)" value={t.extensions.used.toLocaleString()} />
          <Tile label="Free to assign" value={t.extensions.free.toLocaleString()} />
          <Tile label="DID 8XXXX used" value={t.extensions.did_8xxxx_used.toLocaleString()} sub="562-570-XXXX" />
          <Tile label="DID 8XXXX free" value={t.extensions.did_8xxxx_free.toLocaleString()} sub="562-570-XXXX" />
        </div>
        <p className="text-xs text-gray-500 mt-3">Full per-user detail, MAC addresses, Unity VM, hunt pilots and the searchable used/free index live in the Master sheet.</p>
      </div>

      {/* dashboard overview — read-only; editing happens in Migration Tracker Sheet */}
      <TrackerGrid me={me} readOnly />
    </div>
  );
}
